import { GenerateSlidesModal } from "@/components/lessonGenerator/GenerateSlidesModal";
import {
  type DraftJob,
  StatusPill,
  audioProgress,
  isActionable,
  whenItRan,
} from "@/components/lessonGenerator/draftStatus";
import { NotFound } from "@/components/shared/NotFound";
import { MainSidebar } from "@/components/sidebars/MainSidebar";
import { BreadcrumbSkeleton } from "@/components/skeletons/BreadcrumbSkeleton";
import { GetLessonBreadcrumbsDocument } from "@/generated/graphql";
import { useLessonAiUsage } from "@/lib/hooks/aiUsage/useAiUsage";
import { useLessonGenerationJobs } from "@/lib/hooks/lessonGenerator/useLessonGeneration";
import { DEFAULT_TWD_PER_USD, formatCostPerSlide, formatTwd } from "@/lib/utils/aiCost";
import { describeAiError } from "@/lib/utils/aiCredits";
import { hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { ErrorCodes } from "@ai-teach/types";
import { Breadcrumb, Button, LoadingSpinner } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";

function DraftRow({
  job,
  onResume,
  onDiscard,
}: {
  job: DraftJob;
  onResume: () => void;
  onDiscard: () => void;
}) {
  const written = job.writtenSlideIds.length > 0;
  const isRunning = job.status === "pending" || job.status === "running";
  const audio = audioProgress(job);

  return (
    <li className="flex items-center gap-4 rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-800">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill job={job} />
          <span className="text-sm text-secondary-200">
            {written
              ? `${job.writtenSlideIds.length} slides added`
              : `${job.draft.length || job.progress.total} slides`}
          </span>
          <span className="text-xs text-gray-500">{whenItRan(job.createdAt)}</span>
          {job.usage &&
            (job.usage.calls > 0 ? (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 font-mono text-[11px] text-gray-600 dark:bg-white/5 dark:text-gray-300">
                {formatTwd(job.usage.costUsd)}
              </span>
            ) : (
              <span className="text-[11px] text-gray-400">cost not recorded</span>
            ))}
        </div>

        {job.usage && job.usage.calls > 0 && (
          <p className="mt-1 text-xs text-gray-500">
            {job.usage.calls} model call{job.usage.calls === 1 ? "" : "s"} ·{" "}
            {formatCostPerSlide(job.usage.costUsd, job.usage.slidesUsable)}
            {job.usage.wastedCostUsd > 0 &&
              ` · ${formatTwd(job.usage.wastedCostUsd)} redrafted away`}
          </p>
        )}
        {isRunning && (
          <p className="mt-1 text-xs text-gray-500">
            {job.progress.done} of {job.progress.total} drafted
          </p>
        )}
        {audio && (
          <p className="mt-1 text-xs text-gray-500">
            {audio.done} of {audio.total} slides have their audio
          </p>
        )}
        {job.error && <p className="mt-1 text-xs text-error-500">{describeAiError(job.error)}</p>}
      </div>

      <div className="flex shrink-0 gap-2">
        {job.status === "done" && !written && (
          <Button size="sm" onClick={onResume}>
            Review
          </Button>
        )}
        {!written && (
          <Button variant="secondary" size="sm" onClick={onDiscard}>
            Discard
          </Button>
        )}
      </div>
    </li>
  );
}

export function LessonDraftsPage() {
  const { lessonId } = useParams({ from: "/admin/lesson/$lessonId/drafts" });
  const [resumeJobId, setResumeJobId] = useState<string | null>(null);

  const {
    data,
    loading: breadcrumbsLoading,
    error,
  } = useQuery(GetLessonBreadcrumbsDocument, {
    variables: { id: lessonId },
    fetchPolicy: "cache-first",
  });

  const { jobs, loading, refetch, discard } = useLessonGenerationJobs(lessonId);
  const { usage } = useLessonAiUsage(lessonId);
  const waiting = jobs.filter(isActionable);

  if (hasGraphQLErrorCode(error, ErrorCodes.NOT_FOUND)) {
    return <NotFound title="Lesson not found" description="This lesson doesn't exist." />;
  }

  const breadcrumbs = data?.lesson?.breadcrumbs;

  return (
    <div className="flex h-screen">
      <MainSidebar />
      <div className="flex-1 overflow-auto">
        <div className="mx-auto max-w-4xl p-6">
          {breadcrumbsLoading ? (
            <BreadcrumbSkeleton className="mb-5" />
          ) : (
            <Breadcrumb
              items={[
                { label: "Home", to: "/admin" },
                ...(breadcrumbs?.slice(0, -1).map((one) => ({
                  label: one.name,
                  to: `/admin/project/${one.id}`,
                })) ?? []),
                { label: breadcrumbs?.at(-1)?.name ?? "", to: `/admin/lesson/${lessonId}` },
                { label: "Drafts" },
              ]}
              className="mb-4"
            />
          )}

          <h1 className="mt-5 text-2xl font-bold">Generated drafts</h1>

          <p className="mb-5 mt-1 text-xs text-gray-500 dark:text-gray-400">
            {usage && usage.calls > 0 ? (
              <>
                <strong className="font-semibold text-gray-900 dark:text-gray-100">
                  {formatTwd(usage.costUsd)}
                </strong>{" "}
                of LLM spend on this lesson, all time ·{" "}
                {formatCostPerSlide(usage.costUsd, usage.slidesUsable)} ·{" "}
                {formatTwd(usage.wastedCostUsd)} on drafts nobody kept ·{" "}
                <Link
                  to="/admin/ai-usage"
                  search={{
                    groupBy: "template",
                    days: 0,
                    rate: DEFAULT_TWD_PER_USD,
                    lesson: lessonId,
                  }}
                  className="font-medium text-primary-600 hover:underline dark:text-primary-300"
                >
                  breakdown
                </Link>
              </>
            ) : (
              "No LLM spend recorded for this lesson yet."
            )}
          </p>

          {loading && jobs.length === 0 ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : waiting.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 px-4 py-12 text-center dark:border-gray-700">
              <p className="body-sm text-gray-500 dark:text-gray-400">
                No drafts waiting for this lesson.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {waiting.map((job) => (
                <DraftRow
                  key={job.id}
                  job={job}
                  onResume={() => setResumeJobId(job.id)}
                  onDiscard={() => discard(job.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {resumeJobId && !breadcrumbsLoading && (
        <GenerateSlidesModal
          isOpen={true}
          onClose={() => setResumeJobId(null)}
          lessonId={lessonId}
          resumeJobId={resumeJobId}
          onSlidesAdded={refetch}
        />
      )}
    </div>
  );
}
